import React, { useContext, useEffect, useRef, useState } from "react";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModal from "../TransferTicketModal";
import ChangeTicketLineModal from "../ChangeTicketLineModal";
import toastError from "../../errors/toastError";
import { Can } from "../Can";
import { AuthContext } from "../../context/Auth/AuthContext";

const TicketOptionsMenu = ({ ticket, menuOpen, handleClose, anchorEl }) => {
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
  const [changeLineModalOpen, setChangeLineModalOpen] = useState(false);
  const [lines, setLines] = useState([]);
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);

  useEffect(() => () => { isMounted.current = false; }, []);

  // Las líneas se piden al abrir el menú, no al montar la cabecera del chat:
  // antes cada ticket que se abría disparaba un GET /whatsapp/ solo para
  // decidir si dibujar un selector que casi nadie tocaba.
  useEffect(() => {
    if (!menuOpen || lines.length > 0) return;
    const loadLines = async () => {
      try {
        const { data } = await api.get("/whatsapp/");
        if (!isMounted.current) return;
        setLines(
          data.filter(w =>
            ["whatsapp", "whatsapp_cloud", "whatsapp_coexistence"].includes(w.channel)
          )
        );
      } catch (err) {
        toastError(err);
      }
    };
    loadLines();
  }, [menuOpen, lines.length]);

  const handleDeleteTicket = async () => {
    try { await api.delete(`/tickets/${ticket.id}`); }
    catch (err) { toastError(err); }
  };

  const handleOpenConfirmationModal = () => { setConfirmationOpen(true); handleClose(); };
  const handleOpenTransferModal = () => { setTransferTicketModalOpen(true); handleClose(); };
  const handleOpenChangeLineModal = () => { setChangeLineModalOpen(true); handleClose(); };
  const handleCloseChangeLineModal = () => { if (isMounted.current) setChangeLineModalOpen(false); };
  const handleCloseTransferTicketModal = () => { if (isMounted.current) setTransferTicketModalOpen(false); };

  if (!menuOpen) return (
    <>
      <ConfirmationModal
        title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${ticket.id} ${i18n.t("ticketOptionsMenu.confirmationModal.titleFrom")} ${ticket.contact?.name}?`}
        open={confirmationOpen}
        onClose={setConfirmationOpen}
        onConfirm={handleDeleteTicket}
      >
        {i18n.t("ticketOptionsMenu.confirmationModal.message")}
      </ConfirmationModal>
      <TransferTicketModal modalOpen={transferTicketModalOpen} onClose={handleCloseTransferTicketModal} ticketid={ticket.id} ticketWhatsappId={ticket.whatsappId} />
      <ChangeTicketLineModal modalOpen={changeLineModalOpen} onClose={handleCloseChangeLineModal} ticket={ticket} lines={lines} />
    </>
  );

  return (
    <>
      {menuOpen && anchorEl && (
        <div className="fixed inset-0 z-40" onClick={handleClose}>
          <div
            className="absolute bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg py-1 z-50 min-w-40"
            style={{ top: anchorEl.getBoundingClientRect().bottom + 4, right: window.innerWidth - anchorEl.getBoundingClientRect().right }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleOpenTransferModal}
              className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              {i18n.t("ticketOptionsMenu.transfer")}
            </button>
            {lines.length > 1 && (
              <button
                onClick={handleOpenChangeLineModal}
                className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                {i18n.t("ticketOptionsMenu.changeLine")}
              </button>
            )}
            <Can
              role={user.profile}
              perform="ticket-options:deleteTicket"
              permissions={user.permissions}
              yes={() => (
                <button
                  onClick={handleOpenConfirmationModal}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                >
                  {i18n.t("ticketOptionsMenu.delete")}
                </button>
              )}
            />
          </div>
        </div>
      )}
      <ConfirmationModal
        title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${ticket.id} ${i18n.t("ticketOptionsMenu.confirmationModal.titleFrom")} ${ticket.contact?.name}?`}
        open={confirmationOpen}
        onClose={setConfirmationOpen}
        onConfirm={handleDeleteTicket}
      >
        {i18n.t("ticketOptionsMenu.confirmationModal.message")}
      </ConfirmationModal>
      <TransferTicketModal modalOpen={transferTicketModalOpen} onClose={handleCloseTransferTicketModal} ticketid={ticket.id} ticketWhatsappId={ticket.whatsappId} />
      <ChangeTicketLineModal modalOpen={changeLineModalOpen} onClose={handleCloseChangeLineModal} ticket={ticket} lines={lines} />
    </>
  );
};

export default TicketOptionsMenu;
